import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  OptionType,
  onCheckBoxGroupChange,
} from "@portal/BaseComponents/CheckBox/type";
import { Flex } from "@portal/BaseComponents/Flex/Flex";
import { Input } from "@portal/BaseComponents/Input/AntInput";
import { OnInputChange } from "@portal/BaseComponents/Input/type";
import { debounce, uniq } from "lodash";
import { useCallback, useState } from "react";
import { MultiSelect, MultiSelectProps } from "./MultiSelect";

export type SearchOptions<T> = {
  [P in keyof T]: T[P];
};
type attributeKeys<F> = keyof F;
export type MultiSelectSearch<T> =
  | { type: "local"; placeholder: string }
  | {
      type: "Remote";
      placeholder: string;
      shouldDebounce?: boolean;
      onSearch(value: string): Promise<SearchOptions<T>[]>;
      valueAttribute: attributeKeys<T>;
      labelAttribute: attributeKeys<T> | attributeKeys<T>[];
    };

interface MultiSelectElementProps<T> {
  multiSelectProps: MultiSelectProps;
  search?: false | MultiSelectSearch<T>;
}

const ClearIcon = (
  <FontAwesomeIcon icon={["far", "times-circle"]} color="var(--gray-5)" />
);
const SearchIcon = (
  <FontAwesomeIcon icon={["far", "search"]} color="var(--gray-9)" />
);
function MultiSelectElement<T>(props: MultiSelectElementProps<T>) {
  const { multiSelectProps, search = false } = props;
  const [defaultOptions] = useState(multiSelectProps.options);
  const [computedOptions, setComputedOptions] = useState(
    multiSelectProps.options
  );
  const [loading, setLoading] = useState(false);
  const [selectedValues, setSelectedValues] = useState<
    OptionType["value"][] | undefined
  >(multiSelectProps.initialValue);

  function getSearchCallback() {
    if (!search) return;

    return search.type === "Remote"
      ? debounce(
          async function handleSearch(...args: OnInputChange) {
            const [event] = args;
            setLoading(true);
            if (event.target.value === "") {
              updatedCallBack();
              setLoading(false);
              return;
            }
            try {
              const data = await search.onSearch(event.target.value);
              const searchOptions: MultiSelectProps["options"] = data.map(
                (option) => {
                  return {
                    label: Array.isArray(search.labelAttribute)
                      ? `${option[search.labelAttribute[0]]} ${
                          option[search.labelAttribute[1]]
                        }`
                      : String(option[search.labelAttribute]),
                    value: String(option[search.valueAttribute]),
                  };
                }
              );
              setLoading(false);
              setComputedOptions(searchOptions);
            } catch {
              setLoading(false);
              throw new Error("something went wrong");
            }
          },
          search.shouldDebounce ? 300 : undefined
        )
      : function handleSearch(...args: OnInputChange) {
          const [event] = args;

          if (event.target.value === "" && !selectedValues?.length) {
            updatedCallBack();
          }

          const searchOptions = defaultOptions.filter((option) =>
            String(option.label)
              .toLocaleLowerCase()
              .includes(event.target.value)
          );
          setComputedOptions(searchOptions);
        };
  }

  function handleUpdateOptions() {
    const options = computedOptions.filter((f) =>
      selectedValues?.includes(f.value)
    );

    setComputedOptions(uniq([...options, ...defaultOptions]));
  }

  const updatedCallBack = useCallback(handleUpdateOptions, [
    computedOptions,
    defaultOptions,
    selectedValues,
  ]);
  const searchCallBack = useCallback(getSearchCallback, [
    defaultOptions,
    search,
    selectedValues?.length,
    updatedCallBack,
  ]);

  const handleSearchCallback = searchCallBack();

  function handleCancel() {
    multiSelectProps.onCancel?.();
    updatedCallBack();
  }
  function handleSubmit(value: OptionType["value"][], unassigned?: boolean) {
    updatedCallBack();
    multiSelectProps.onSubmit(value, unassigned);
  }

  function handleChange(...args: onCheckBoxGroupChange) {
    const [value] = args;
    setSelectedValues(value);
  }
  return (
    <>
      {search ? (
        <Flex vertical space="16" align="center">
          <Input
            type="text"
            onChange={handleSearchCallback}
            placeholder={`${search.placeholder}...`}
            size="middle"
            prefix={SearchIcon}
            allowClear={{ clearIcon: ClearIcon }}
          />
          <MultiSelect
            {...multiSelectProps}
            options={computedOptions}
            loading={loading}
            onCancel={handleCancel}
            onSubmit={handleSubmit}
            onChange={handleChange}
          />
        </Flex>
      ) : (
        <MultiSelect {...multiSelectProps} />
      )}
    </>
  );
}

export { MultiSelectElement };
